import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthProvider';

interface RequireAuthProps {
  children: JSX.Element;
  adminOnly?: boolean;
  /**
   * Require ActBlue access (admin AND not a board member). Board Members are
   * bounced to /not-authorized — defense-in-depth for the ActBlue routes whose
   * nav/cards are already hidden from them.
   */
  actBlueOnly?: boolean;
}

export default function RequireAuth({ children, adminOnly = false, actBlueOnly = false }: RequireAuthProps) {
  const navigate = useNavigate();
  const { user, loading, isAdmin, canManageActBlue, adminChecked } = useAuth();
  const [ready, setReady] = useState(false);

  // Any route requiring the admin check to have resolved before deciding.
  const needsAdminCheck = adminOnly || actBlueOnly;

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    if (needsAdminCheck) {
      if (!adminChecked) return;
      if (!isAdmin) {
        navigate('/not-authorized');
        return;
      }
      if (actBlueOnly && !canManageActBlue) {
        navigate('/not-authorized');
        return;
      }
    }

    setReady(true);
  }, [loading, user, needsAdminCheck, actBlueOnly, isAdmin, canManageActBlue, adminChecked, navigate]);

  if (loading || (needsAdminCheck && !adminChecked) || !ready) {
    return <div className="flex items-center justify-center h-screen text-lg">Loading...</div>;
  }

  return children;
}
